import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Plus, Mail, Phone, Calendar, Eye, ArrowUpDown, Filter, UserCheck, Clock, FileText, Upload, Trash2, X, Save, Edit2, Heart, AlertTriangle, Droplet, Trophy } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { StatsCard } from '../components/common/StatsCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useApp } from '../contexts/AppContext';
import api from '../services/api';
import { toast } from 'sonner';

const subjects = ['Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology', 'English', 'Hindi', 'History', 'Geography', 'Civics', 'Economics', 'Computer Science', 'Physical Education', 'Arts', 'Music', 'Other'];

function getInitials(name) {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || 'T';
}

export function TeachersPage() {
  const navigate = useNavigate();
  const { isAdmin, currentUser } = useApp();
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [stats, setStats] = useState(null);

  // On Leave modal
  const [isOnLeaveOpen, setIsOnLeaveOpen] = useState(false);

  // Add Teacher modal
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '', email: '', phone: '', subject: '', experience: '', qualifications: '',
    age: '', gender: '', password: 'teacher123',
    emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelation: '',
    medicalConditions: '', bloodGroup: '',
  });
  const [addErrors, setAddErrors] = useState({});
  const [isAdding, setIsAdding] = useState(false);

  // Teacher Profile modal
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isMedicalEditing, setIsMedicalEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Document upload on profile
  const [docFile, setDocFile] = useState(null);
  const [docType, setDocType] = useState('resume');
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  useEffect(() => { fetchTeachers(); }, []);

  const fetchTeachers = async () => {
    setIsLoading(true);
    try {
      const [list, statsData] = await Promise.all([
        api.listTeachers(),
        api.getTeacherStats().catch(() => null),
      ]);
      setTeachers(list);
      setStats(statsData);
    } catch (e) { toast.error('Failed to load teachers'); }
    finally { setIsLoading(false); }
  };

  const todayOnLeave = useMemo(() => {
    if (!stats?.onLeaveToday) return [];
    return stats.onLeaveToday;
  }, [stats]);

  const filteredTeachers = useMemo(() => {
    let list = teachers.filter(t => {
      const matchSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) || (t.subject || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (statusFilter === 'active') return matchSearch && !todayOnLeave.some(l => l.teacherId === t.userId);
      if (statusFilter === 'on_leave') return matchSearch && todayOnLeave.some(l => l.teacherId === t.userId);
      return matchSearch;
    });
    list.sort((a, b) => {
      let vA = a[sortField] || '', vB = b[sortField] || '';
      if (typeof vA === 'string') { vA = vA.toLowerCase(); vB = (vB || '').toLowerCase(); }
      if (vA < vB) return sortDir === 'asc' ? -1 : 1;
      if (vA > vB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [teachers, searchTerm, statusFilter, sortField, sortDir, todayOnLeave]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Add teacher
  const validateAdd = () => {
    const e = {};
    if (!addForm.name.trim()) e.name = 'Required';
    if (!addForm.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email)) e.email = 'Invalid email';
    if (addForm.phone && !/^\d{10,15}$/.test(addForm.phone.replace(/[^0-9]/g, ''))) e.phone = 'Invalid phone';
    setAddErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddTeacher = async () => {
    if (!validateAdd()) return;
    setIsAdding(true);
    try {
      await api.createTeacher({
        ...addForm,
        age: addForm.age ? parseInt(addForm.age) : undefined,
      });
      toast.success(`${addForm.name} added successfully`);
      setIsAddOpen(false);
      setAddForm({ name: '', email: '', phone: '', subject: '', experience: '', qualifications: '', age: '', gender: '', password: 'teacher123', emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelation: '', medicalConditions: '', bloodGroup: '' });
      setAddErrors({});
      fetchTeachers();
    } catch (e) { toast.error(e.message || 'Failed to add teacher'); }
    finally { setIsAdding(false); }
  };

  // Profile
  const openProfile = async (teacher) => {
    setIsProfileLoading(true);
    setSelectedTeacher(null);
    try {
      const profile = await api.getTeacherProfile(teacher.userId);
      setSelectedTeacher(profile);
    } catch (e) { toast.error('Failed to load teacher profile'); }
    finally { setIsProfileLoading(false); }
  };

  const startEditing = () => {
    const t = selectedTeacher;
    setEditForm({
      name: t.name || '', email: t.email || '', phone: t.phone || '', subject: t.subject || '',
      experience: t.experience || '', qualifications: t.qualifications || '',
      age: t.age || '', gender: t.gender || '',
      emergencyContactName: t.emergencyContactName || '', emergencyContactPhone: t.emergencyContactPhone || '',
      emergencyContactRelation: t.emergencyContactRelation || '',
      medicalConditions: t.medicalConditions || '', bloodGroup: t.bloodGroup || '',
    });
    setIsEditing(true);
  };

  const startMedicalEditing = () => {
    const t = selectedTeacher;
    setEditForm({ medicalConditions: t.medicalConditions || '', bloodGroup: t.bloodGroup || '' });
    setIsMedicalEditing(true);
  };

  const handleSaveMedical = async () => {
    setIsSaving(true);
    try {
      await api.updateTeacher(selectedTeacher.userId, { medicalConditions: editForm.medicalConditions, bloodGroup: editForm.bloodGroup });
      toast.success('Medical info updated');
      setIsMedicalEditing(false);
      const profile = await api.getTeacherProfile(selectedTeacher.userId);
      setSelectedTeacher(profile);
    } catch (e) { toast.error(e.message || 'Failed to update'); }
    finally { setIsSaving(false); }
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await api.updateTeacher(selectedTeacher.userId, { ...editForm, age: editForm.age ? parseInt(editForm.age) : undefined });
      toast.success('Profile updated');
      setIsEditing(false);
      const profile = await api.getTeacherProfile(selectedTeacher.userId);
      setSelectedTeacher(profile);
      fetchTeachers();
    } catch (e) { toast.error(e.message || 'Failed to update'); }
    finally { setIsSaving(false); }
  };

  const handleDocUpload = async () => {
    if (!docFile || !selectedTeacher) return;
    setIsUploadingDoc(true);
    try {
      await api.uploadTeacherDocument(selectedTeacher.userId, docType, docFile);
      toast.success('Document uploaded');
      setDocFile(null);
      const profile = await api.getTeacherProfile(selectedTeacher.userId);
      setSelectedTeacher(profile);
    } catch (e) { toast.error(e.message || 'Upload failed'); }
    finally { setIsUploadingDoc(false); }
  };

  const handleDeleteDoc = async (fileId) => {
    try {
      await api.deleteDocument(fileId);
      toast.success('Document deleted');
      const profile = await api.getTeacherProfile(selectedTeacher.userId);
      setSelectedTeacher(profile);
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="teachers-page">
      <PageHeader title="Teachers" description="Manage teaching staff">
        {isAdmin && (
          <Button className="bg-brand-deep-blue hover:bg-brand-deep-blue/90" onClick={() => setIsAddOpen(true)} data-testid="add-teacher-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Teacher
          </Button>
        )}
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatsCard title="Total Teachers" value={stats?.total || teachers.length} icon={Users} delay={0} />
        <StatsCard title="Active Today" value={stats?.active || (teachers.length - todayOnLeave.length)} icon={UserCheck} delay={0.05} />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-pointer" onClick={() => todayOnLeave.length > 0 && setIsOnLeaveOpen(true)} data-testid="card-on-leave">
                <StatsCard title="On Leave Today" value={todayOnLeave.length} icon={Clock} delay={0.1} className={todayOnLeave.length > 0 ? 'ring-1 ring-amber-400/50' : ''} />
              </div>
            </TooltipTrigger>
            <TooltipContent><p>{todayOnLeave.length > 0 ? 'Click to view teachers currently on leave' : 'No teachers on leave today'}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <StatsCard title="Avg Experience" value={stats?.avgExperience ? `${stats.avgExperience} yrs` : '-'} icon={Calendar} delay={0.15} />
      </div>

      {/* On Leave List Dialog */}
      <Dialog open={isOnLeaveOpen} onOpenChange={setIsOnLeaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /> Teachers On Leave Today</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[400px] overflow-y-auto">
            {todayOnLeave.length === 0 ? (
              <p className="text-sm text-muted-foreground">No teachers on leave today.</p>
            ) : todayOnLeave.map((leave, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800" data-testid={`leave-teacher-${i}`}>
                <div>
                  <p className="text-sm font-medium">{leave.teacherName}</p>
                  <p className="text-xs text-muted-foreground">{leave.leaveType || 'Leave'} &middot; {leave.startDate} to {leave.endDate}</p>
                </div>
                <Badge variant="outline" className="text-amber-600 border-amber-300">{leave.status}</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters & Table */}
      <DataCard>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search teachers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="teacher-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="teacher-status-filter">
              <Filter className="w-4 h-4 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teachers</SelectItem>
              <SelectItem value="active">Active Today</SelectItem>
              <SelectItem value="on_leave">On Leave</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTeachers.map(teacher => {
              const isOnLeave = todayOnLeave.some(l => l.teacherId === teacher.userId);
              return (
                <div key={teacher.userId}
                  className={`relative p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${isOnLeave ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'}`}
                  onClick={() => openProfile(teacher)}
                  data-testid={`teacher-card-${teacher.userId}`}
                >
                  {isOnLeave && <Badge className="absolute top-2 right-2 bg-amber-500 text-white text-[10px]">On Leave</Badge>}
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarFallback className="bg-brand-deep-blue/10 text-brand-deep-blue text-sm font-medium">{getInitials(teacher.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm break-words" title={teacher.name}>{teacher.name}</p>
                      <p className="text-xs text-muted-foreground">{teacher.subject || 'General'}</p>
                      {teacher.email && <p className="text-xs text-muted-foreground break-all line-clamp-1" title={teacher.email}>{teacher.email}</p>}
                      {teacher.phone && <p className="text-xs text-muted-foreground">{teacher.phone}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {teacher.assignedClasses?.slice(0, 3).map(c => (
                      <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>
                    ))}
                    {teacher.assignedClasses?.length > 3 && <Badge variant="outline" className="text-[10px]">+{teacher.assignedClasses.length - 3}</Badge>}
                    {teacher.experience && <Badge variant="secondary" className="text-[10px]">{teacher.experience} {teacher.experience && !String(teacher.experience).toLowerCase().includes('yr') ? '(in years)' : ''}</Badge>}
                  </div>
                </div>
              );
            })}
            {filteredTeachers.length === 0 && (
              <div className="col-span-full text-center py-12 text-muted-foreground">No teachers found</div>
            )}
          </div>
        )}
      </DataCard>

      {/* Teacher Profile Dialog */}
      <Dialog open={!!selectedTeacher || isProfileLoading} onOpenChange={() => { setSelectedTeacher(null); setIsEditing(false); setIsMedicalEditing(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {isProfileLoading ? (
            <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-brand-light-blue border-t-transparent rounded-full animate-spin" /></div>
          ) : selectedTeacher && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-lg">{selectedTeacher.name}</DialogTitle>
                    <DialogDescription>{selectedTeacher.subject || 'General Teacher'} &middot; {selectedTeacher.email}</DialogDescription>
                  </div>
                  {isAdmin && !isEditing && (
                    <Button variant="outline" size="sm" onClick={startEditing} data-testid="edit-teacher-btn">
                      <Edit2 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  )}
                  {!isAdmin && currentUser?.role === 'teacher' && selectedTeacher?.userId === currentUser?.userId && !isMedicalEditing && (
                    <Button variant="outline" size="sm" onClick={startMedicalEditing} data-testid="edit-medical-btn">
                      <Heart className="w-3 h-3 mr-1" /> Update Medical Info
                    </Button>
                  )}
                </div>
              </DialogHeader>

              {isEditing ? (
                <div className="space-y-4 mt-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-teacher-name" /></div>
                    <div><Label>Email</Label><Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} data-testid="edit-teacher-email" /></div>
                    <div><Label>Phone</Label><Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value.replace(/[^0-9+\-\s]/g, '')})} /></div>
                    <div><Label>Subject</Label>
                      <Select value={editForm.subject || 'none'} onValueChange={v => setEditForm({...editForm, subject: v === 'none' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Subject" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">-</SelectItem>{subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Experience (in years)</Label><Input value={editForm.experience} onChange={e => setEditForm({...editForm, experience: e.target.value})} /></div>
                    <div><Label>Qualifications</Label><Input value={editForm.qualifications} onChange={e => setEditForm({...editForm, qualifications: e.target.value})} /></div>
                    <div><Label>Age</Label><Input type="number" value={editForm.age} onChange={e => setEditForm({...editForm, age: e.target.value})} /></div>
                    <div><Label>Gender</Label>
                      <Select value={editForm.gender || 'none'} onValueChange={v => setEditForm({...editForm, gender: v === 'none' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Gender" /></SelectTrigger>
                        <SelectContent><SelectItem value="none">-</SelectItem><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-muted-foreground border-b pb-1">Medical Profile</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Blood Group</Label>
                      <Select value={editForm.bloodGroup || 'none'} onValueChange={v => setEditForm({...editForm, bloodGroup: v === 'none' ? '' : v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">-</SelectItem>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Medical Conditions</Label><Input value={editForm.medicalConditions} onChange={e => setEditForm({...editForm, medicalConditions: e.target.value})} /></div>
                  </div>
                  <p className="text-xs font-medium text-muted-foreground border-b pb-1">Emergency Contact</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Name</Label><Input value={editForm.emergencyContactName} onChange={e => setEditForm({...editForm, emergencyContactName: e.target.value})} /></div>
                    <div><Label>Phone</Label><Input value={editForm.emergencyContactPhone} onChange={e => setEditForm({...editForm, emergencyContactPhone: e.target.value.replace(/[^0-9+\-\s]/g, '')})} /></div>
                    <div><Label>Relation</Label><Input value={editForm.emergencyContactRelation} onChange={e => setEditForm({...editForm, emergencyContactRelation: e.target.value})} /></div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsEditing(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
                    <Button onClick={handleSaveEdit} disabled={isSaving} className="bg-brand-deep-blue" data-testid="save-teacher-btn"><Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving...' : 'Save'}</Button>
                  </div>
                </div>
              ) : isMedicalEditing ? (
                <div className="space-y-4 mt-3">
                  <p className="text-xs font-medium text-muted-foreground border-b pb-1">Update Medical Profile</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Blood Group</Label>
                      <Select value={editForm.bloodGroup || 'none'} onValueChange={v => setEditForm({...editForm, bloodGroup: v === 'none' ? '' : v})}>
                        <SelectTrigger data-testid="medical-blood-group"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">-</SelectItem>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Medical Conditions</Label><Input value={editForm.medicalConditions} onChange={e => setEditForm({...editForm, medicalConditions: e.target.value})} data-testid="medical-conditions-input" /></div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsMedicalEditing(false)}><X className="w-4 h-4 mr-1" /> Cancel</Button>
                    <Button onClick={handleSaveMedical} disabled={isSaving} className="bg-brand-deep-blue" data-testid="save-medical-btn"><Save className="w-4 h-4 mr-1" /> {isSaving ? 'Saving...' : 'Save'}</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5 mt-3">
                  {/* Summary row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                      <p className="text-xl font-bold text-brand-deep-blue">{selectedTeacher.studentCount || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Students</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                      <p className="text-xl font-bold">{selectedTeacher.assignedClasses?.length || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Classes</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                      <p className="text-xl font-bold">{selectedTeacher.attendanceSummary?.attendanceMarkedCount || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Attendance Marked (30d)</p>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                      <p className="text-xl font-bold">{selectedTeacher.experience || '-'}</p>
                      <p className="text-[10px] text-muted-foreground">Experience (in years)</p>
                    </div>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 text-sm">
                      <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Contact</h4>
                      <p><span className="text-muted-foreground">Phone:</span> {selectedTeacher.phone || '-'}</p>
                      <p><span className="text-muted-foreground">Email:</span> <span className="break-all">{selectedTeacher.email || '-'}</span></p>
                      {selectedTeacher.age && <p><span className="text-muted-foreground">Age:</span> {selectedTeacher.age}</p>}
                      {selectedTeacher.gender && <p><span className="text-muted-foreground">Gender:</span> {selectedTeacher.gender}</p>}
                      <p><span className="text-muted-foreground">Qualifications:</span> {selectedTeacher.qualifications || '-'}</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Heart className="w-3 h-3 text-red-500" /> Medical Profile</h4>
                      <p><span className="text-muted-foreground">Blood Group:</span> {selectedTeacher.bloodGroup || '-'}</p>
                      <p><span className="text-muted-foreground">Medical Conditions:</span> {selectedTeacher.medicalConditions || 'None reported'}</p>
                      <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide pt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-500" /> Emergency Contact</h4>
                      <p><span className="text-muted-foreground">Name:</span> {selectedTeacher.emergencyContactName || '-'}</p>
                      <p><span className="text-muted-foreground">Phone:</span> {selectedTeacher.emergencyContactPhone || '-'}</p>
                      <p><span className="text-muted-foreground">Relation:</span> {selectedTeacher.emergencyContactRelation || '-'}</p>
                    </div>
                  </div>

                  {/* Leave History */}
                  {selectedTeacher.leaves?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide mb-2">Leave History</h4>
                      <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                        {selectedTeacher.leaves.map((leave, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                            <div>
                              <span className="font-medium">{leave.leaveType}</span>
                              <span className="text-muted-foreground ml-2">{leave.startDate} — {leave.endDate}</span>
                            </div>
                            <Badge className={leave.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : leave.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'} variant="outline">
                              {leave.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents */}
                  <div>
                    <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1"><FileText className="w-3 h-3" /> Documents</h4>
                    {isAdmin && (
                      <div className="flex items-end gap-2 mb-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex-1">
                          <Label className="text-xs">Document Type</Label>
                          <Select value={docType} onValueChange={setDocType}>
                            <SelectTrigger className="h-8 text-xs" data-testid="teacher-doc-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="resume">Resume</SelectItem>
                              <SelectItem value="id_proof">ID Proof</SelectItem>
                              <SelectItem value="qualification_cert">Qualification Certificate</SelectItem>
                              <SelectItem value="experience_cert">Experience Certificate</SelectItem>
                              <SelectItem value="medical_cert">Medical Certificate</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs">File (<span className="text-brand-deep-blue font-semibold underline">Browse</span>)</Label>
                          <Input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setDocFile(e.target.files[0])} className="h-8 text-xs" data-testid="teacher-doc-file" />
                        </div>
                        <Button size="sm" onClick={handleDocUpload} disabled={!docFile || isUploadingDoc} className="bg-brand-deep-blue h-8 text-xs" data-testid="teacher-upload-doc-btn">
                          <Upload className="w-3 h-3 mr-1" /> {isUploadingDoc ? '...' : 'Upload'}
                        </Button>
                      </div>
                    )}
                    {selectedTeacher.documents?.length > 0 ? (
                      <div className="space-y-1.5">
                        {selectedTeacher.documents.map((doc, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 border rounded text-sm" data-testid={`teacher-doc-${i}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{doc.fileName}</p>
                                <p className="text-[10px] text-muted-foreground capitalize">{doc.docType?.replace(/_/g, ' ')}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {doc.fileUrl && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(doc.fileUrl, '_blank')} title="View">
                                  <Eye className="w-3 h-3" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteDoc(doc.fileId)} title="Delete" data-testid={`delete-teacher-doc-${i}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No documents uploaded.</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Teacher Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Add New Teacher</DialogTitle>
            <DialogDescription>Fill in teacher details. Fields marked * are required.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2">
              <Label>Full Name *</Label>
              <Input value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value.replace(/[0-9]/g, '')})} data-testid="add-teacher-name" className={addErrors.name ? 'border-red-500' : ''} />
              {addErrors.name && <p className="text-xs text-red-500">{addErrors.name}</p>}
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} data-testid="add-teacher-email" className={addErrors.email ? 'border-red-500' : ''} />
              {addErrors.email && <p className="text-xs text-red-500">{addErrors.email}</p>}
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={addForm.phone} onChange={e => setAddForm({...addForm, phone: e.target.value.replace(/[^0-9+\-\s]/g, '')})} data-testid="add-teacher-phone" className={addErrors.phone ? 'border-red-500' : ''} />
              {addErrors.phone && <p className="text-xs text-red-500">{addErrors.phone}</p>}
            </div>
            <div>
              <Label>Age</Label>
              <Input type="number" min="18" max="70" value={addForm.age} onChange={e => setAddForm({...addForm, age: e.target.value})} data-testid="add-teacher-age" />
            </div>
            <div>
              <Label>Gender</Label>
              <Select value={addForm.gender || 'none'} onValueChange={v => setAddForm({...addForm, gender: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="add-teacher-gender"><SelectValue placeholder="Gender" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-</SelectItem><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Select value={addForm.subject || 'none'} onValueChange={v => setAddForm({...addForm, subject: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="add-teacher-subject"><SelectValue placeholder="Subject" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-</SelectItem>{subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Experience (in years)</Label>
              <Input value={addForm.experience} onChange={e => setAddForm({...addForm, experience: e.target.value})} placeholder="e.g., 5" data-testid="add-teacher-exp" />
            </div>
            <div>
              <Label>Qualifications</Label>
              <Input value={addForm.qualifications} onChange={e => setAddForm({...addForm, qualifications: e.target.value})} placeholder="e.g., M.Sc, B.Ed" data-testid="add-teacher-qual" />
            </div>
            <div>
              <Label>Blood Group</Label>
              <Select value={addForm.bloodGroup || 'none'} onValueChange={v => setAddForm({...addForm, bloodGroup: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Blood Group" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-</SelectItem>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs font-medium text-muted-foreground border-b pb-1 col-span-2">Emergency Contact</p>
            <div><Label>Name</Label><Input value={addForm.emergencyContactName} onChange={e => setAddForm({...addForm, emergencyContactName: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={addForm.emergencyContactPhone} onChange={e => setAddForm({...addForm, emergencyContactPhone: e.target.value.replace(/[^0-9+\-\s]/g, '')})} /></div>
            <div><Label>Relation</Label><Input value={addForm.emergencyContactRelation} onChange={e => setAddForm({...addForm, emergencyContactRelation: e.target.value})} /></div>
            <div><Label>Medical Conditions</Label><Input value={addForm.medicalConditions} onChange={e => setAddForm({...addForm, medicalConditions: e.target.value})} /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTeacher} disabled={isAdding} className="bg-brand-deep-blue" data-testid="confirm-add-teacher-btn">{isAdding ? 'Adding...' : 'Add Teacher'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
